import { ApiShopperLandingPageLink } from '@storefronts/common-types'
import { useContext, useEffect, useMemo } from 'react'
import { LandingPageLinksContext } from '../context/LandingPageLinksContext'
import { normalizeLinkHashes } from '../utils/resolveLandingPageLinksByHash'

/**
 * Resolves landing page links for the given hashes from the page payload / session cache.
 * Fetches only hashes that are not already on the landing page.
 */
export const useLandingPageLinksForHashes = (
    linkHashes?: readonly string[] | null,
): ApiShopperLandingPageLink[] => {
    const { getCachedOrderedLinksForHashes, fetchAndCacheLinksForHashes } =
        useContext(LandingPageLinksContext)

    // Callers often pass a new array each render (`[section.linkHash]`). Key by
    // value so the fetch effect does not re-run unless the hashes actually change.
    // eslint-disable-next-line react-you-might-not-need-an-effect/no-event-handler
    const hashesKey = normalizeLinkHashes(linkHashes).join('\0')
    const hashes = useMemo(
        () => (hashesKey === '' ? [] : hashesKey.split('\0')),
        [hashesKey],
    )

    const { orderedLinks, allHashesResolved } = useMemo(
        () => getCachedOrderedLinksForHashes(hashes),
        [getCachedOrderedLinksForHashes, hashes],
    )

    useEffect(() => {
        if (allHashesResolved) return
        void fetchAndCacheLinksForHashes(hashes)
    }, [allHashesResolved, fetchAndCacheLinksForHashes, hashes])

    return orderedLinks
}
